// PRO Drift Auditor -- per-holding drift & 3-way risk-contribution diagnostics.
//
// For every holding: target vs final-drifted vs AI-optimal weight (mini tri-bar),
// absolute drift, trailing 6m momentum (diverging bar), downside vol, and the
// Target/Drifted/AI PCTR risk-contribution decomposition.

import React, { useMemo } from 'react';
import { Crosshair } from 'lucide-react';

import { Card } from './Card';
import { SymbolLink } from './SymbolLink';
import { fmtPct, fmtPctSigned, signClass } from '../format';
import type { ProHoldingDriftDiagnostic } from '../types';

// Diverging momentum bar centred on zero.
const MomentumBar: React.FC<{ value: number; max: number }> = ({ value, max }) => {
    const half = max || 1;
    const widthPct = Math.min(100, (Math.abs(value) / half) * 50);
    const positive = value >= 0;
    return (
        <div className="relative h-4 w-28 rounded bg-slate-100 dark:bg-slate-800">
            <div className="absolute left-1/2 top-0 h-full w-px bg-slate-300 dark:bg-slate-600" />
            <div
                className={`absolute top-0 h-full rounded ${positive ? 'bg-emerald-500/70' : 'bg-rose-500/70'}`}
                style={{
                    width: `${widthPct}%`,
                    left: positive ? '50%' : `${50 - widthPct}%`,
                }}
            />
        </div>
    );
};

// Tri-segment weight comparison bar.
const WeightTriBar: React.FC<{ target: number; drifted: number; ai: number }> = ({ target, drifted, ai }) => {
    const max = Math.max(target, drifted, ai, 1);
    const seg = (v: number, color: string, label: string) => (
        <div className="flex items-center gap-1.5">
            <div className="h-1.5 w-24 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${(v / max) * 100}%`, background: color }} />
            </div>
            <span className="w-11 text-right text-[11px] tabular-nums text-slate-500 dark:text-slate-400" title={label}>
                {v.toFixed(1)}%
            </span>
        </div>
    );
    return (
        <div className="space-y-1">
            {seg(target, '#94a3b8', 'Target')}
            {seg(drifted, '#f43f5e', 'Drifted')}
            {seg(ai, '#10b981', 'Forge Ultra optimal')}
        </div>
    );
};

export const HoldingDiagnosticsTable: React.FC<{
    rows: ProHoldingDriftDiagnostic[];
    routes?: Record<string, string>;
}> = ({ rows, routes }) => {
    const maxMom = useMemo(
        () => Math.max(1, ...rows.map((r) => Math.abs(r.trailing_6m_momentum_pct))),
        [rows],
    );

    return (
        <Card
            title="Holding-Level Drift & Risk Diagnostics"
            subtitle="Target vs drifted vs Forge Ultra-optimal weights, trailing 6-month momentum, and the 3-way risk-contribution (PCTR) decomposition."
            icon={<Crosshair className="h-4 w-4" />}
            bodyClassName=""
        >
            <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                    <thead>
                        <tr className="bg-slate-50 dark:bg-slate-800/50 border-y border-slate-100 dark:border-slate-800 text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                            <th className="px-3.5 py-2.5 text-left">Ticker</th>
                            <th className="px-3.5 py-2.5 text-left">Weights (Tgt / Drift / Forge Ultra)</th>
                            <th className="px-3.5 py-2.5 text-right">Drift</th>
                            <th className="px-3.5 py-2.5 text-center">6M Momentum</th>
                            <th className="px-3.5 py-2.5 text-right">6M Downside Vol</th>
                            <th className="px-3.5 py-2.5 text-right">PCTR Tgt</th>
                            <th className="px-3.5 py-2.5 text-right">PCTR Drift</th>
                            <th className="px-3.5 py-2.5 text-right">PCTR Forge Ultra</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => (
                            <tr
                                key={r.ticker}
                                className="border-b border-slate-50 dark:border-slate-800/60 last:border-0 hover:bg-slate-50/60 dark:hover:bg-slate-800/40"
                            >
                                <td className="px-3.5 py-3 whitespace-nowrap">
                                    <SymbolLink symbol={r.ticker} routes={routes} />
                                </td>
                                <td className="px-3.5 py-3">
                                    <WeightTriBar
                                        target={r.target_weight_pct}
                                        drifted={r.final_drifted_weight_pct}
                                        ai={r.ai_regime_optimal_weight_pct}
                                    />
                                </td>
                                <td className={`px-3.5 py-3 text-right tabular-nums font-semibold ${signClass(r.absolute_drift_pct)}`}>
                                    {fmtPctSigned(r.absolute_drift_pct)}
                                </td>
                                <td className="px-3.5 py-3">
                                    <div className="flex items-center justify-center gap-2">
                                        <MomentumBar value={r.trailing_6m_momentum_pct} max={maxMom} />
                                        <span className={`w-14 text-right tabular-nums text-xs ${signClass(r.trailing_6m_momentum_pct)}`}>
                                            {fmtPctSigned(r.trailing_6m_momentum_pct, 1)}
                                        </span>
                                    </div>
                                </td>
                                <td className="px-3.5 py-3 text-right tabular-nums text-slate-600 dark:text-slate-300">
                                    {fmtPct(r.trailing_6m_downside_vol_pct, 1)}
                                </td>
                                <td className="px-3.5 py-3 text-right tabular-nums text-slate-500 dark:text-slate-400">
                                    {fmtPct(r.risk_contribution_target_pct, 1)}
                                </td>
                                <td className="px-3.5 py-3 text-right tabular-nums text-rose-600 dark:text-rose-400">
                                    {fmtPct(r.risk_contribution_drifted_pct, 1)}
                                </td>
                                <td className="px-3.5 py-3 text-right tabular-nums text-emerald-600 dark:text-emerald-400 font-semibold">
                                    {fmtPct(r.risk_contribution_ai_pro_pct, 1)}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </Card>
    );
};
